import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NotifierService } from 'angular-notifier';
import { firstValueFrom, switchMap } from 'rxjs';
import { BoobleModalService } from 'booble-modal';
import { CancelModal } from 'src/app/shared/modals/cancel/cancel-modal.component';
import { RegisterStepType } from 'src/app/domain/enums/register-step-types.enum';
import { CompanyPartnerDto } from 'src/app/domain/models/dto/company-partner.dto';
import { CompanyResponsibleDto } from 'src/app/domain/models/dto/company-responsible.dto';
import { AuthenticationService } from 'src/app/shared/services';
import { CompanyResponsibleService } from 'src/app/shared/services/company-responsible.service';
import { RegisterService } from 'src/app/shared/services/register.service';
import { CorporateAccountService } from 'src/app/shared/services/corporate-account.service';
import { OnboardingSuccessModalComponent } from '../responses/onboarding-success-modal/onboarding-success-modal.component';
import { CorporateAccountDto } from 'src/app/domain/models/dto/corporate-account.dto';
import { IndividualAccountService } from 'src/app/shared/services/individual-account.service';

type Document = {
  id: number;
  file?: File;
  valid?: boolean;
  loading?: boolean;
};

@Component({
  selector: 'app-responsible-documents',
  templateUrl: './responsible-documents.component.html',
  styleUrls: ['./responsible-documents.component.scss'],
})
export class ResponsibleDocumentsComponent implements OnInit {
  private readonly notifier: NotifierService;
  tenant: string = '';
  public loading: boolean;
  confirmData = false;

  socios: CompanyPartnerDto[] = [];
  responsibles: CompanyResponsibleDto[] = [];
  responsiblesDocuments: Document[] = [];
  allResponsiblesDocuments: boolean;
  corporateAccountDocument: any = null;
  company: CorporateAccountDto;
  @ViewChild('documentFile') documentFileRef: ElementRef;
  @ViewChild('responsibleFile') responsibleFileRef: ElementRef;
  isMobile: boolean;
  get companyId() {
    return +this.authService.getCompanyId();
  }

  get userId() {
    return this.authService.getCurrentUserId();
  }

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    notifierService: NotifierService,
    private readonly authService: AuthenticationService,
    private readonly registerService: RegisterService,
    private readonly individualAccountService: IndividualAccountService,
    private readonly corporateaccountService: CorporateAccountService,
    private readonly companyResponsibleService: CompanyResponsibleService,
    private readonly modalService: BoobleModalService
  ) {
    this.notifier = notifierService;
    this.companyResponsibleService.getByCompany().subscribe({
      next: (corporateAccountResult) => {
        corporateAccountResult.data.forEach((responsible) => {
          if (
            responsible?.participation >= 25 ||
            responsible?.hasCompanyControl
          ) {
            this.responsibles.push(responsible);
            this.responsiblesDocuments.push({ id: responsible.id, file: null });
          }
        });
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  ngOnInit(): void {
    this.corporateaccountService.get().subscribe((getResult) => {
      this.company = getResult.data;
    });
    this.route.paramMap.subscribe((params) => {
      const result = params.get('tenant');

      if (result) this.tenant = params.get('tenant');
    });

    this.isMobile = window.innerWidth < 820 ? true : false;
  }

  resetDocumentFile() {
    this.documentFileRef.nativeElement.value = '';
    this.corporateAccountDocument = null;
  }

  resetResponsibleFile(id: number) {
    this.responsibleFileRef.nativeElement.value = '';
    this.getResponsibleDocumentById(id).file = null;
  }

  async uploadDocumentFile(id: number, event: any) {
    const element = event.currentTarget as HTMLInputElement;
    let fileList: FileList | null = element.files;
    if (fileList) {
      this.corporateAccountDocument = fileList[0];
    }
  }

  async uploadResponsibleFile(responsible: CompanyResponsibleDto, event: any) {
    const element = event.currentTarget as HTMLInputElement;
    const file = element.files?.[0];
    const doc = this.getResponsibleDocumentById(responsible?.id);

    if (!file || !doc) return;

    doc.file = file;
    doc.valid = false;
    doc.loading = true;

    try {
      const { data } = await firstValueFrom(
        this.individualAccountService.validateIndividualDocument(
          responsible?.document,
          file
        )
      );

      doc.valid = data === true;

      this.notifier.notify(
        doc.valid ? 'success' : 'error',
        doc.valid
          ? `Documento validado com sucesso!`
          : `Documento inválido. Tente novamente.`
      );
    } catch {
      doc.valid = false;
      this.notifier.notify('error', 'Erro ao validar documento.');
    } finally {
      doc.loading = false;
      this.updateAllResponsiblesValid();
    }
  }

  updateAllResponsiblesValid() {
    this.allResponsiblesDocuments = this.responsiblesDocuments.every(
      (d) => d.file && d.valid
    );
  }

  getResponsibleDocumentById(id: number): Document | null {
    return this.responsiblesDocuments.find((r) => r.id === id);
  }

  async submit() {
    this.loading = true;

    try {
      await firstValueFrom(
        this.corporateaccountService.uploadSocialContract(
          this.companyId,
          this.corporateAccountDocument!
        )
      );

      this.responsiblesDocuments.forEach(async (doc, index) => {
        await firstValueFrom(
          this.registerService.uploadDocument(doc.id, doc.file!)
        );
        if (index === this.responsiblesDocuments.length - 1) {
          await firstValueFrom(
            this.authService
              .account()
              .pipe(
                switchMap((account) =>
                  this.registerService.registerSeeaway(account.id)
                )
              )
          );
          this.goNext();
        }
      });
    } catch (error) {
      this.loading = false;
    }
  }

  // async uploadProcurationFile(id: number, event: any) {
  //   const element = event.currentTarget as HTMLInputElement;
  //   let fileList: FileList | null = element.files;
  //   if (fileList) {
  //     await firstValueFrom(
  //       this.registerService.uploadProcurationFile(id, fileList.item(0))
  //     );
  //   }
  // }

  goBack() {
    this.router.navigate([`${this.tenant}/onboardings/bank-account`]);
  }

  goNext() {
    this.loading = true;
    this.registerService
      .putRegisterStep(RegisterStepType.ResponsibleDocuments)
      .subscribe({
        next: (stepResult) => {
          this.openSuccessModal();
          this.router.navigate([`${this.tenant}/home`]);
        },
        error: (err) => {
          this.loading = false;
        },
      });
  }

  formInvalid(): boolean {
    return (
      !this.confirmData ||
      !this.corporateAccountDocument ||
      this.responsiblesDocuments.some((d) => !d.file || !d.valid)
    );
  }

  goPrevious() {
    this.router.navigate([`${this.tenant}/onboardings/first-limit`]);
  }

  openCancelModal() {
    this.modalService.present(CancelModal);
  }

  openSuccessModal() {
    this.modalService.present(OnboardingSuccessModalComponent);
  }
}
